<!-- 消息记录、消息模板和消息配置展示。数据请求、编辑和启停由父页面处理。 -->
<template>
  <section class="message-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span>{{ sectionHint }}</span>
        </div>
      </div>

      <div v-if="section === 'logs'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>消息场景</th>
              <th>接收人</th>
              <th>渠道</th>
              <th>状态</th>
              <th>发送时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in logPage?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ getMessageSceneText(item.scene) }}</span></td>
              <td><span class="cell-text">{{ item.receiver || '--' }}</span></td>
              <td><span class="cell-text">{{ getMessageChannelText(item.channel) }}</span></td>
              <td><span :class="['status-tag', toneClass(messageLogStatusTone(item.status))]">{{ getMessageLogStatusText(item.status) }}</span></td>
              <td>{{ item.sentAt || '--' }}</td>
            </tr>
            <tr v-if="!(logPage?.list.length) && !loadingLogs"><td colspan="5"><el-empty description="暂无消息记录" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'templates'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>模板编码</th>
              <th>消息场景</th>
              <th>标题</th>
              <th>状态</th>
              <th>更新时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in templates" :key="item.id">
              <td><span class="cell-text">{{ item.code }}</span></td>
              <td><span class="cell-text">{{ getMessageSceneText(item.scene) }}</span></td>
              <td><span class="cell-text">{{ item.title || '--' }}</span></td>
              <td><span :class="['status-tag', toneClass(messageTemplateStatusTone(item.status))]">{{ getMessageTemplateStatusText(item.status) }}</span></td>
              <td>{{ item.updatedAt || '--' }}</td>
              <td class="is-right"><div class="row-actions"><button title="编辑模板" @click="emit('editTemplate', item)"><el-icon><EditPen /></el-icon></button></div></td>
            </tr>
            <tr v-if="!templates.length && !loadingTemplates"><td colspan="6"><el-empty description="暂无消息模板" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>消息场景</th>
              <th>渠道</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in configs" :key="item.id">
              <td><span class="cell-text">{{ getMessageSceneText(item.scene) }}</span></td>
              <td><span class="cell-text">{{ getMessageChannelText(item.channel) }}</span></td>
              <td><span :class="['status-tag', toneClass(messageConfigTone(item.enabled))]">{{ getMessageConfigEnabledText(item.enabled) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button :title="item.enabled ? '停用配置' : '启用配置'" :disabled="messageConfigSubmitting" @click="emit('toggleConfig', item)">
                    <el-icon><EditPen /></el-icon>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!configs.length && !loadingConfig"><td colspan="4"><el-empty description="暂无消息配置" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="section === 'logs' && logPage" class="table-footer">
        <span>共 {{ logPage.total }} 条记录，第 {{ logPage.page }}/{{ logTotalPages }} 页</span>
        <div>
          <button :disabled="logPage.page <= 1" @click="emit('changeLogPage', logPage.page - 1)">上一页</button>
          <button class="is-active">{{ logPage.page }}</button>
          <button :disabled="!logPage.hasMore" @click="emit('changeLogPage', logPage.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import { EditPen } from '@element-plus/icons-vue'
import type { MessageConfig, MessageTemplate } from '@/api/message'
import type { OperationStat } from '@/data/operationDesign'

type MessageLogPage = Awaited<ReturnType<typeof import('@/api/message').fetchMessageLogs>>
type MessageSection = 'logs' | 'templates' | 'config'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: MessageSection
  stats: OperationStat[]
  logPage?: MessageLogPage
  templates: MessageTemplate[]
  configs: MessageConfig[]
  loadingLogs: boolean
  loadingTemplates: boolean
  loadingConfig: boolean
  messageConfigSubmitting: boolean
  logTotalPages: number
  getMessageSceneText: (scene?: string) => string
  getMessageChannelText: (channel?: string) => string
  getMessageLogStatusText: (status?: string) => string
  getMessageTemplateStatusText: (status?: string) => string
  getMessageConfigEnabledText: (enabled?: boolean) => string
  messageLogStatusTone: (status?: string) => StatusTone
  messageTemplateStatusTone: (status?: string) => StatusTone
  messageConfigTone: (enabled?: boolean) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  changeLogPage: [page: number]
  editTemplate: [item: MessageTemplate]
  toggleConfig: [item: MessageConfig]
}>()

const {
  section,
  stats,
  logPage,
  templates,
  configs,
  loadingLogs,
  loadingTemplates,
  loadingConfig,
  messageConfigSubmitting,
  logTotalPages,
  getMessageSceneText,
  getMessageChannelText,
  getMessageLogStatusText,
  getMessageTemplateStatusText,
  getMessageConfigEnabledText,
  messageLogStatusTone,
  messageTemplateStatusTone,
  messageConfigTone,
  toneClass,
} = toRefs(props)

const sectionHint = computed(() => {
  if (section.value === 'logs') return '消息发送记录已接入后端分页，便于核对场景、渠道和发送状态。'
  if (section.value === 'templates') return '消息模板支持真实查询与新增、编辑，启停状态会同步回写后端。'
  return '消息配置按场景维护启停状态，当前直接使用后端场景配置进行更新。'
})

</script>

<style scoped lang="scss">
.message-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button { height: 36px; padding: 0 16px; border-radius: 8px; background: var(--jf-color-primary); color: #fff; font-size: 14px; font-weight: 700; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button:disabled, .table-footer button:disabled { cursor: not-allowed; opacity: .5; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
